import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as MarketingUser from '@/services/rest-api/lib/customer/marketing-user'

import { GET_CITIES } from '@/services/api/master/address'
import { formatIndexDate } from '@/helpers/tzDate'

import CONTRACT_TYPES, { CONTRACT_TYPE } from '../constants/contract_type'
import CONTRACT_PERIODS from '../constants/contract_period'
import { TMS_MARKETING_USER_FILTER } from '@/helpers/tms-access'

export default {
  apollo: {
    getCities: {
      query: GET_CITIES,
      fetchPolicy: 'no-cache',
      variables: {
        filter: {
          pagination: { page: 1, show: 999 },
        },
      },
      result({ data }) {
        this.cities = data?.data?.cities?.data ?? []
      },
    },
  },
  data() {
    return {
      cities: [],
    }
  },
  computed: {
    baseInfoFields() {
      return [
        {
          key: 'customer_id',
          name: 'customer_id',
          label: 'Nama Pelanggan',
          type: 'select-resourceful-v2',
          validation: 'required',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          placeholder: 'Pilih nama pelanggan',
          trackBy: 'id',
          optionsGetter: (opts) => Customer.get({ ...opts, sorts: ['name asc'] }),
          optionLabelFn: ({ item }) => item.name,
          disabled: this.isLoading || ['Dikonfirmasi'].includes(this.customerContractStatus),
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: this.customerContractStatus === 'Temporary',
          formatter: (item) => item.name,
        },
        {
          key: 'third_party_id',
          name: 'third_party_id',
          label: 'ID Referensi',
          type: 'text',
          placeholder: ['Dikonfirmasi'].includes(this.customerContractStatus)
            ? ''
            : 'Masukkan ID Referensi',
          disabled: this.isLoading || ['Dikonfirmasi'].includes(this.customerContractStatus),
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
        },
        {
          key: 'contract_source',
          name: 'contract_source',
          label: 'Asal Kontrak',
          readOnly: true,
        },
        {
          key: 'reference',
          name: 'reference',
          label: 'Diperbarui dari',
          readOnly: true,
          formatter: (item) => item?.number || '-',
          handleOpen: (item) => {
            if (!item?.id || !item?.number) return null
            const routeData = this.$router.resolve({
              name: 'Master Customer Contract Detail',
              params: {
                contractId: item.id,
              },
            })
            return routeData.href
          },
        },
      ]
    },
    contractRequirementFields() {
      return [
        {
          key: 'contract_type',
          name: 'contract_type',
          label: 'Jenis Kontrak',
          type: 'select-resourceful-v2',
          validation: 'required',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          placeholder: 'Pilih jenis kontrak',
          trackBy: 'key',
          optionsGetter: async (opts) => {
            const searchTerm = opts?.search?.toLowerCase() || ''
            const contractTypes = CONTRACT_TYPES.slice()

            return {
              metadata: {
                count: 4,
                page: 1,
                total_count: 4,
                total_page: 1,
              },
              data: {
                paginated_result: contractTypes.filter((item) =>
                  item.name.toLowerCase().includes(searchTerm)
                ),
              },
            }
          },
          optionLabelFn: ({ item }) => item.name,
          disabled: this.isLoading || ['Dikonfirmasi'].includes(this.customerContractStatus),
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: this.customerContractStatus === 'Temporary',
          formatValue: (value) => CONTRACT_TYPE?.[value]?.name ?? '-',
        },
        {
          key: 'city',
          name: 'city',
          label: 'Kota',
          type: 'select-resourceful-v2',
          validation: 'required',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          placeholder: 'Pilih kota',
          trackBy: 'name',
          disabled: this.isLoading,
          optionsGetter: this.fetchCities,
          optionLabelFn: ({ item }) => item.name,
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: ['Temporary', 'Draf', 'Dikonfirmasi'].includes(this.customerContractStatus),
        },
        {
          key: 'custodian_id',
          name: 'custodian_id',
          label: 'Penanggung Jawab',
          type: 'select-resourceful-v2',
          validation: 'required',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          placeholder: 'Pilih penanggung jawab',
          trackBy: 'id',
          disabled: this.isLoading,
          optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
          optionLabelFn: ({ item }) => item.name,
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: ['Temporary', 'Draf', 'Dikonfirmasi'].includes(this.customerContractStatus),
          formatter: (item) => item?.name || '-',
        },
        {
          key: 'contract_duration',
          name: 'contract_duration',
          label: 'Durasi Kontrak',
          type: 'number',
          _type: 'number',
          validation: 'bail|required|number|min:1,value',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          placeholder: 'Masukkan Durasi Kontrak',
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: ['Temporary', 'Draf', 'Dikonfirmasi'].includes(this.customerContractStatus),
          disabled: this.isLoading,
          suffixField: true,
          suffix: {
            key: 'contract_period',
            name: 'contract_period',
            type: 'select-resourceful-v2',
            trackBy: 'key',
            hasIconSearch: false,
            isMandatory: true,
            disabled: this.isLoading,
            optionsGetter: async (opts) => {
              const searchTerm = opts?.search?.toLowerCase() || ''

              const contractPeriodUnits = CONTRACT_PERIODS.slice()

              return {
                metadata: {
                  count: 4,
                  page: 1,
                  total_count: 4,
                  total_page: 1,
                },
                data: {
                  paginated_result: contractPeriodUnits.filter((item) =>
                    item.name.toLowerCase().includes(searchTerm)
                  ),
                },
              }
            },
            optionLabelFn: ({ item }) => item?.name,
          },
          formatter: (_, value) => `${value} ${this.fullObjects?.['contract_period']?.name}`,
        },
        {
          key: 'effective_date',
          name: 'effective_date',
          label: 'Tanggal Mulai',
          type: 'date-picker',
          placeholder: 'Pilih Tanggal Mulai',
          valueType: 'YYYY-MM-DD',
          validation: 'required',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          format: 'DD MMM YYYY',
          disabled: this.isLoading,
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: ['Temporary', 'Draf', 'Dikonfirmasi'].includes(this.customerContractStatus),
          formatValue: (value) => {
            if (!value) return null
            return formatIndexDate(value).date
          },
        },
        {
          key: 'expired_date',
          name: 'expired_date',
          label: 'Tanggal Berakhir',
          helper: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus)
            ? null
            : 'Pilih tanggal mulai terlebih dahulu',
          readOnly: true,
          disabled: this.isLoading,
          computeValue: (formValues) => {
            if (!formValues?.effective_date) return null

            const date = new Date(formValues.effective_date)
            const duration = parseInt(formValues.contract_duration) || 0

            switch (formValues.contract_period) {
              case 'MONTHLY':
                date.setMonth(date.getMonth() + duration)
                break
              case 'YEARLY':
                date.setFullYear(date.getFullYear() + duration)
                break
            }

            return new Date(date).toISOString().split('T')[0]
          },
          formatComputedValue: (value) => {
            if (!value) return null
            return formatIndexDate(value).date
          },
          dependsOn: ['effective_date', 'contract_duration', 'contract_period'],
        },
        {
          key: 'max_mileage',
          name: 'max_mileage',
          label: 'Jarak Tempuh Maks. / Bulan',
          type: 'number',
          _type: 'number',
          placeholder: '0',
          validation: 'bail|required|number|min:1,value',
          validationBehavior: 'live',
          errorBehavior: 'blur',
          suffix: 'km',
          disabled: this.isLoading,
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
          help: ['Temporary', 'Draf', 'Dikonfirmasi'].includes(this.customerContractStatus),
          formatter: (_, value) => `${value}`,
        },
        {
          key: 'total_vehicle',
          name: 'total_vehicle',
          label: 'Jumlah Kendaraan',
          helper: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus)
            ? null
            : 'Simpan & tambahkan kendaraan terlebih dahulu',
          readOnly: true,
          disabled: this.isLoading,
          computeValue: (formValues) => {
            if (!formValues?.total_vehicle) return null

            return `${formValues.total_vehicle}`
          },
          formatComputedValue: (value) => {
            if (!value) return null

            return value
          },
          dependsOn: ['total_vehicle'],
        },
      ]
    },
    otherFields() {
      return [
        {
          key: 'note',
          name: 'note',
          type: 'textarea',
          label: 'Catatan',
          placeholder: 'Masukkan catatan',
          disabled: this.isLoading,
          readOnly: ['Kadaluwarsa', 'Dibatalkan', 'Ditutup'].includes(this.customerContractStatus),
        },
      ]
    },
  },
  created() {
    // Dynamically set up watchers based on field dependencies
    this.contractRequirementFields.forEach((field) => {
      if (field.dependsOn && field.computeValue) {
        field.dependsOn.forEach((dep) => {
          this.$watch(`formValues.${dep}`, () => {
            const computed = field.computeValue(this.formValues)
            this.$set(this.formValues, field.name, computed)
          })
        })
      }
    })
  },
  methods: {
    getFieldDisplayValue(field, model) {
      // Use field's custom computation if available
      if (field.computeValue && field.dependsOn) {
        const computedVal = field.computeValue(this.formValues)
        if (computedVal) {
          // If field has a formatter for computed value, use it
          if (field.formatComputedValue) {
            return field.formatComputedValue(computedVal)
          }
          // Otherwise return as-is
          return computedVal
        }
        return null
      }

      // Use formatValue if available
      if (field.formatValue && !this.isEmpty(this.formValues[field.name])) {
        return field.formatValue(this.formValues[field.name])
      }

      if (field?.formatter) {
        return field.formatter(this.fullObjects?.[field.name], model)
      }

      return model
    },
    async fetchCities(opts) {
      const citiesData = this.cities.map(({ name }) => ({ name }))

      const searchTerm = opts?.search?.toLowerCase() || ''

      const filteredCities = searchTerm
        ? citiesData.filter((item) => item.name.toLowerCase().includes(searchTerm))
        : citiesData

      return {
        metadata: {
          count: filteredCities.length,
          page: 1,
          total_count: filteredCities.length,
          total_page: 1,
        },
        data: {
          paginated_result: filteredCities,
        },
      }
    },
  },
}
